import {
    DefaultButton, IPanelProps, IRenderFunction, IStackProps, Panel,
    PanelType, PivotItem, PrimaryButton, Stack, useTheme
} from "@fluentui/react";
import { RootState } from "app/store";
import ControlMask from "components/controls/ControlMask";
import TextFieldControl from "components/controls/TextFieldControl";
import ToggleControl from "components/controls/ToggleControl";
import { FieldsToLookupByMasterAttributeId, friendlyValueToFlexibleFieldValue, IFlexibleFieldWithAttributes, panelButtonsStyles } from "global/Application";
import { TChangeTracker } from "global/Reducer/dataSlice";
import { ParentVisibilityGraphProcessor } from "global/Visibility/ParentVisibilityGraph";
import * as IXrmTypes from 'model/CRM';
import * as FAM from 'model/FlexibleAttributeModel';
import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import FlexibleFieldView, { EScopeType } from "./FlexibleFieldView";
import { bcbs_config_masterattribute } from "model/AttributeOptionSets.type";
import { EControlType } from "components/ControlWrapper";
import SectionItem, { ESectionItemVariant } from "components/controls/SectionItem/Index";
import ValidationDialog from "./ValidationDialog";
import { EValidationState, IValidationModel } from "./ValidationDialog/ValidationDialog.type";
import { EValidationSeverity } from "components/controls/ValidationView/ValidationView.type";

export enum EVariant {
    panel, viewer
}

type TValidationModel = IValidationModel;


interface IProps extends TComponentConnectedProps {
    isOpen: boolean;
    onDismiss?: any;
    defaultValue: FAM.INonStandard | null;
    benefit?: FAM.IBenefit;
    onChange?: (value: FAM.INonStandard, changeTracker: TChangeTracker) => void;
    costshareCategory?: IXrmTypes.bcbs_config_category;
    disabled?: boolean;
    variant?: EVariant;
}

const NonStandardEditor: React.FC<IProps> = (props) => {
    const [nonStandard, setNonStandard] = useState<FAM.INonStandard | null>(null);

    const [changeTracker, setChangeTracker] = useState<TChangeTracker>({});

    const parentVisibilityProcessor = useMemo(() => {
        const gProcessor = new ParentVisibilityGraphProcessor(
            props.state.data.config,
            props.state.data.config.cache.parentVisibilityGraph
        );

        return gProcessor;
    }, []);

    // allows the component to perform a local visibility management, to help with the animation and ui smoothness
    // by default this should always be visible unless components has signaled a context job termination and wants to self close
    const [selfVisible, setSelfVisible] = useState(true);

    const categoryNonStandard = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard'];
    const theme = useTheme();

    useEffect(() => {
        if (props.isOpen) {
            setSelfVisible(true);

            setChangeTracker({});

            setValidationsVM(null);

            if (props.defaultValue)
                setNonStandard(t => {
                    const value = structuredClone(props.defaultValue);

                    if (value?.id === 'bulk') {
                        //@ts-ignore
                        delete value?.deactivated;
                    }

                    return value;
                });
        }

    }, [props.isOpen, props.defaultValue]);

    const onRenderProperties: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        if (!nonStandard)
            return <></>

        const isBulk = nonStandard?.id === 'bulk';

        return <PivotItem
            headerText={"Properties"}
            key={"nonStandardProperties"}
        >
            <SectionItem
                title="General"
                variant={ESectionItemVariant.Drawer}
            >
                <TextFieldControl
                    label={"Name"}
                    disabled={isBulk || props.disabled || nonStandard.partOfTemplate}
                    fieldValueOnChange={(event, value) => {
                        setNonStandard(t => {
                            return {
                                ...t!,
                                name: value.value ?? ''
                            };
                        })
                    }}
                    defaultFieldValue={{ type: 'string', value: nonStandard.name ?? '' }}
                />

                <ControlMask
                    label="BCBS Reference Only"
                    defaultLocked={isBulk}
                    onUnlock={() => {
                        setNonStandard(t => { return { ...t!, deactivated: false }; });
                    }}
                >
                    <ToggleControl
                        label={"BCBS Reference Only"}
                        checked={nonStandard.referenceOnly ?? false}
                        disabled={props.disabled}
                        onChange={(_, checked) => {
                            setNonStandard(t => { return { ...t!, referenceOnly: checked ?? false }; });
                        }}
                    />
                </ControlMask>

                <ControlMask
                    label="Deactivate"
                    defaultLocked={isBulk}
                    onUnlock={() => {
                        setNonStandard(t => { return { ...t!, deactivated: false }; });
                    }}
                >
                    <ToggleControl
                        label={"Deactivate"}
                        checked={nonStandard.deactivated ?? false}
                        disabled={props.disabled || nonStandard.partOfTemplate}
                        onChange={(_, checked) => {
                            setNonStandard(t => { return { ...t!, deactivated: checked ?? false }; });
                        }}
                    />
                </ControlMask>

                {/* <Dropdown
                
                    label={"Status"}
                    options={statusOptions}
                    selectedKey={groupNumber?.status?.value}
                    onChange={(_, option?: IDropdownOption<FAM.TStatusOptions>) => {
                        setGroupNumber(oldState => {
                            return { ...oldState!, status: option?.data! };
                        });
                    }}
                /> */}

            </SectionItem>
        </PivotItem>
    }

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {

        if (!props.defaultValue)
            return <div>unexpected state</div>

        // for rendering we have two options
        // Option A - start rendering with known attributes and find fields
        //  small number of fields, so should be able to cache this in:
        // const attributes = props.state.config.cache.attributeSets[props.masterAttribute?.bcbs_config_masterattributeid!];
        // attributes?.forEach(eAttr => {
        //     const masterAttribute = props.state.config.cache.masterAttributesMap[eAttr._bcbs_config_masterattributeid_value!];
        //     const controlType = masterAttribute.bcbs_attributetypecode as EControlType;

        //     const readOnly = eAttr.bcbs_isreadonly ?? false;
        //     disabled = disabled || (controlType === EControlType.message) || (readOnly === true);
        // })

        // Option B - Lets start by fields and find attributes
        // const configAttributes = props.state?.data.config.cache.map_bcbs_config_attribute;
        // const masterAttributes = props.state?.data.config.cache.map_bcbs_config_masterattribute;

        return <FlexibleFieldView
            disabled={props.disabled}
            fields={nonStandard?.fields! ?? []}
            category={categoryNonStandard}
            costshareCategory={props.costshareCategory}
            tiers={props?.benefit?.tiers ?? []}
            bulk={nonStandard?.id === 'bulk'}
            cancellation={false}
            scope={nonStandard}
            scopeType={EScopeType.nonStandard}
            lastPivotItem={[onRenderProperties]}
            onChange={(changeEvent) => {
                if (changeEvent.field) {

                    // TODO used in emit event, but it really should change approach to compare and generate change tracker performing a delta before & after
                    setChangeTracker(xChange => {
                        const nChange: typeof xChange = { ...xChange };
                        nChange[changeEvent.field?.configRef.id!] = {
                            field: changeEvent.field!,
                            fieldValue: changeEvent.fieldValue!,
                            value: changeEvent.value!,
                        }
                        return nChange;
                    });

                    setNonStandard(xNonStandard => {
                        const nNonStandard = { ...xNonStandard! };

                        const fields = nNonStandard.fields;
                        const field = nNonStandard?.fields?.find(q => q.id === changeEvent.field!.id);

                        if (field) {
                            const fieldValue = field?.values?.find(q => q.id === changeEvent.fieldValue!.id);

                            if (fieldValue)
                                fieldValue.value = changeEvent.value!;

                            const graphChanges = parentVisibilityProcessor.processVisibilityOnAttributeChange(
                                props.state.data.config,
                                field,
                                fields
                            );

                            // TODO refactor this to a helper function so you can call the second iteration with a TTL to avoid infinite loops 
                            graphChanges.forEach(eGraphChange => {
                                if (fields)
                                    if (eGraphChange.newVisibility === 0) {

                                        delete fields[eGraphChange.index].visibility;

                                        // TODO refactor, unify all three and clean up
                                        // * Duplicated code, this has been duplicated in addUpdateAttributeSet, AttributeSetGridViewEditor.onChange, NonStandardGridViewEditor.onChange
                                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/319161
                                        fields[eGraphChange.index].values.forEach(eachValue => {

                                            if (eGraphChange.attribute.bcbs_defaultvalue)
                                                switch (eGraphChange.masterAttribute.bcbs_attributetypecode) {

                                                    // ignore, nothing to do, since it is of type message
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Message(808900014)'].value:
                                                        break;

                                                    // ignore, nothing to do, since it is of type message
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Lookup(808900006)'].value:
                                                        throw new Error("Not implemented exception!")
                                                        break;

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Single Line of Text(808900008)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Boolean(808900000)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Option Set(808900007)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Currency(808900002)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Date(808900003)'].value:

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Whole Number(808900010)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Decimal(808900004)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Costshare(808900001)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Vision Costshare(808900012)'].value:

                                                        // blank string or no-value at all
                                                        if (
                                                            !eachValue.value ||
                                                            (eachValue.value.type === 'string' && eachValue.value.value === '')
                                                        ) {

                                                            const field = fields![eGraphChange.index];
                                                            const fieldValue = eachValue;

                                                            const value: FAM.TValue = friendlyValueToFlexibleFieldValue(
                                                                props.state.data.config,
                                                                eGraphChange.masterAttribute.bcbs_attributetypecode as EControlType,
                                                                eGraphChange.attribute.bcbs_defaultvalue!,
                                                                eGraphChange.attribute,
                                                                eGraphChange.masterAttribute,
                                                                props.costshareCategory ?? null,
                                                                null
                                                            );

                                                            setChangeTracker(xChange => {
                                                                const nChange: typeof xChange = { ...xChange };
                                                                nChange[field.configRef.id] = {
                                                                    field: field,
                                                                    fieldValue: structuredClone(fieldValue),
                                                                    value: value!,
                                                                }
                                                                return nChange;
                                                            });

                                                            eachValue.value = value
                                                        }

                                                        break;

                                                    default:
                                                        break;
                                                }


                                        })
                                    }
                                    else {

                                        fields[eGraphChange.index].visibility = eGraphChange.newVisibility;
                                    }
                            });

                        }

                        return nNonStandard;
                    });
                }
            }}
        />
    }

    const onChange = (value: FAM.INonStandard, changeTracker: TChangeTracker) => {
        props.onChange?.(value, changeTracker);
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        return (
            <>
                <PrimaryButton text='Continue'
                    styles={panelButtonsStyles}
                    disabled={props.disabled}
                    onClick={async () => {
                        onContinue();
                    }}
                />
                <DefaultButton text='Cancel'
                    styles={panelButtonsStyles}
                    onClick={() => {
                        props?.onDismiss?.();
                    }}
                />
            </>
        );
    }

    const onContinue = async (xValidationModel?: TValidationModel) => {

        // input validation fallback to state fallback to default
        let nValidationVM: TValidationModel | null = xValidationModel ?? validationVM ?? null

        if (!nValidationVM) {

            // Fields/Values Extraction
            // -----------------------------------------------------
            let bcbs_benefitdefinition_masterfields: IFlexibleFieldWithAttributes[] = [];

            const fields = FieldsToLookupByMasterAttributeId(props.state.data.config,
                nonStandard?.fields ?? [],
                (fields) => {
                    if (fields[0].masterAttribute.bcbs_name?.toLowerCase() === 'benefit definition') {
                        bcbs_benefitdefinition_masterfields = fields;
                    }
                }
            );

            const bcbs_benefitdefinition = bcbs_benefitdefinition_masterfields.length > 0
                ? bcbs_benefitdefinition_masterfields[0].field
                : null;

            const bcbs_benefitdefinition_value = bcbs_benefitdefinition && bcbs_benefitdefinition.values?.[0].value?.type === 'string'
                ? bcbs_benefitdefinition.values?.[0].value
                : null;

            // Validations
            // -----------------------------------------------------
            nValidationVM = {
                validationMessages: [],
                state: EValidationState.Idle,
            }

            // Validation: Non-Standard Required Fields
            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/454644
            if (!nonStandard?.name)
                nValidationVM.validationMessages?.push({
                    type: EValidationSeverity.error,
                    title: 'Nonstandard name is required.',
                    description: 'Several underlying processes use the name to identify nonstandard items.'
                });

            // Validation: Non-Standard Required Fields
            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/454644
            if (bcbs_benefitdefinition && !bcbs_benefitdefinition_value?.value) {
                nValidationVM.validationMessages?.push({
                    type: EValidationSeverity.error,
                    title: 'Benefit Definition is empty.',
                });
            }

            // validation rules
            if (nValidationVM.validationMessages!.length > 0) {
                nValidationVM.state = EValidationState.PendingUser;
            }
            else {
                nValidationVM.state = EValidationState.Fulfilled;
            }
        }

        setValidationsVM(nValidationVM as TValidationModel);

        if (nValidationVM.state === EValidationState.Fulfilled) {
            setSelfVisible(false);
            if (nonStandard)
                onChange(nonStandard, changeTracker);
        }

    }

    const [validationVM, setValidationsVM] = useState<TValidationModel | null>(null);

    const onRenderValidationDialog = () => {
        if (validationVM && validationVM.state === EValidationState.PendingUser)
            return <ValidationDialog
                defaultValue={validationVM}
                onCancel={(xValidationModel) => {
                    setValidationsVM(null);
                }}
                onContinue={(xValidationModel) => {
                    onContinue({ ...xValidationModel, state: EValidationState.Fulfilled });
                }}
            />
    }

    if (props.variant === EVariant.viewer)
        return onRenderBody();

    return (
        <>
            <Panel
                isOpen={props.isOpen && selfVisible}
                onDismiss={props.onDismiss}
                type={PanelType.medium}
                headerText="Non-Standard"
                isLightDismiss
                onRenderFooterContent={onRenderFooter}
                isFooterAtBottom={true}
                key={'nonStandardEditorPanel'}
            >
                <div>
                    {onRenderValidationDialog()}
                    {onRenderBody()}
                </div>
            </Panel>
        </>

    )

}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardEditor)
